import React, { useEffect, useRef } from 'react';

/**
 * StarField - Animated canvas-based star field background
 *
 * @param {number} numStars - Number of stars to render (default: 300)
 * @param {number} maxSpeed - Maximum star movement speed (default: 54.3)
 * @param {number} initialCenterSize - Initial center void size (default: 2)
 */
const StarField = ({ numStars = 300, maxSpeed = 54.3, initialCenterSize = 2 }) => {
  const canvasRef = useRef(null);
  const centerSize = useRef(initialCenterSize);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    // Initialize stars with sparkly effects and slower movement
    const stars = Array.from({ length: numStars }, () => {
      const angle = Math.random() * 2 * Math.PI;
      const radius = Math.random() * canvas.width / 2;
      return {
        angle,
        radius: Math.random() * 3 + 0.5,
        baseRadius: Math.random() * 3 + 0.5,
        speed: Math.random() * (maxSpeed * 0.3) + 0.5,
        angleSpeed: (Math.random() * 0.005 + 0.002) * (Math.random() < 0.5 ? 1 : -1),
        distance: radius,
        opacity: Math.random() * 0.6 + 0.4,
        twinkleSpeed: Math.random() * 0.02 + 0.01,
        twinklePhase: Math.random() * Math.PI * 2,
        brightness: Math.random() * 0.5 + 0.5,
        hue: Math.floor(Math.random() * 60) + 200,
      };
    });

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      stars.forEach(star => {
        star.twinklePhase += star.twinkleSpeed;
        const twinkle = (Math.sin(star.twinklePhase) + 1) / 2;
        const currentOpacity = star.opacity * (0.5 + twinkle * 0.5);
        const currentRadius = star.baseRadius * (0.7 + twinkle * 0.5);

        star.angle += star.angleSpeed;
        star.distance -= star.speed;
        star.x = centerX + star.distance * Math.cos(star.angle);
        star.y = centerY + star.distance * Math.sin(star.angle);

        if (star.distance < centerSize.current) {
          star.distance = Math.random() * canvas.width / 2;
          star.angle = Math.random() * 2 * Math.PI;
          centerSize.current += 0.02;
        }

        const gradient = ctx.createRadialGradient(
          star.x, star.y, 0,
          star.x, star.y, currentRadius * 2
        );

        const sparkleColor = `hsla(${star.hue}, ${60 + twinkle * 40}%, ${85 + twinkle * 15}%, ${currentOpacity})`;

        gradient.addColorStop(0, sparkleColor);
        gradient.addColorStop(0.5, `hsla(${star.hue}, 50%, 70%, ${currentOpacity * 0.6})`);
        gradient.addColorStop(1, `hsla(${star.hue}, 30%, 50%, 0)`);

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(star.x, star.y, currentRadius * 2, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(255, 255, 255, ${currentOpacity * star.brightness})`;
        ctx.beginPath();
        ctx.arc(star.x, star.y, currentRadius * 0.4, 0, Math.PI * 2);
        ctx.fill();
      });

      if (centerSize.current >= Math.sqrt(centerX * centerX + centerY * centerY)) {
        centerSize.current = initialCenterSize;
      }

      requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(draw);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [initialCenterSize, maxSpeed, numStars]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0
      }}
    />
  );
};

export default StarField;
